/**
 * Script loader - loads CDN scripts in correct order
 *
 * Uses HeadlessConfig accessor to read platform status directly
 * from the raw metafield config.
 */

import type { HeadlessConfig } from './headless-config';
import { CDN_BASE, CORE_SCRIPTS, PLATFORM_SCRIPTS, HELPER_SCRIPTS, THIRD_PARTY_SCRIPTS } from './constants';

/** Default timeout for script loading (10 seconds) */
const SCRIPT_TIMEOUT_MS = 10000;

/**
 * Create a timeout promise
 */
function createTimeout(ms: number, url: string): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Script load timeout after ${ms}ms: ${url}`));
    }, ms);
  });
}

/**
 * Load a single script with timeout
 *
 * @param url - Script URL
 * @param attributes - Optional attributes to add to script tag
 * @param timeout - Timeout in milliseconds (default: 10000)
 * @returns Promise that resolves when script loads
 */
export function loadScript(
  url: string,
  attributes?: Record<string, string>,
  timeout: number = SCRIPT_TIMEOUT_MS
): Promise<void> {
  const loadPromise = new Promise<void>((resolve, reject) => {
    // Check if script already exists
    const existing = document.querySelector(`script[src="${url}"]`);
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = false; // Maintain load order
    script.setAttribute('data-cookieconsent', 'ignore');

    // Add custom attributes
    if (attributes) {
      Object.entries(attributes).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });
    }

    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${url}`));

    document.head.appendChild(script);
  });

  // Race between load and timeout
  return Promise.race([loadPromise, createTimeout(timeout, url)]);
}

/**
 * Options for {@link loadScripts}. Bundles the optional integration parameters
 * into one object so callers don't juggle a long positional argument list.
 */
export interface LoadScriptsOptions {
  /** Attribute string to add to injected <script> tags (e.g. 'data-x="y"'). */
  scriptAttribute?: string;
  /** GTM container ID override (for custom GTM setups). */
  gtmContainerId?: string;
  /** Custom GTM server URL (for server-side GTM setups). */
  gtmServerUrl?: string;
  /** Google Consent Mode v2 defaults, set before any Google scripts load. */
  consentDefaults?: {
    ad_storage?: 'granted' | 'denied';
    ad_user_data?: 'granted' | 'denied';
    analytics_storage?: 'granted' | 'denied';
    functionality_storage?: 'granted' | 'denied';
    personalization_storage?: 'granted' | 'denied';
    security_storage?: 'granted' | 'denied';
    wait_for_update?: number;
    ads_data_redaction?: boolean;
  };
  /** Cookiebot CMP ID. */
  cookiebotId?: string;
  /** Full GTM script URL (for Stape server-side GTM). */
  gtmScriptUrl?: string;
  /** Storefront root domain for the headless Shopify Privacy API. */
  storefrontRootDomain?: string;
  /** Checkout root domain for the headless Shopify Privacy API. */
  checkoutRootDomain?: string;
  /** Storefront API access token for the headless Shopify Privacy API. */
  storefrontAccessToken?: string;
}

/**
 * Load all required scripts based on configuration.
 *
 * @param config - HeadlessConfig accessor instance
 * @param options - Optional integration parameters (see {@link LoadScriptsOptions})
 */
export async function loadScripts(
  config: HeadlessConfig,
  {
    scriptAttribute,
    gtmContainerId,
    gtmServerUrl,
    consentDefaults,
    cookiebotId,
    gtmScriptUrl,
    storefrontRootDomain,
    checkoutRootDomain,
    storefrontAccessToken,
  }: LoadScriptsOptions = {}
): Promise<void> {
  const attributes: Record<string, string> = {};

  // Parse script attribute if provided
  if (scriptAttribute) {
    // Handle format like 'data-custom="value"'
    const match = scriptAttribute.match(/^([^=]+)(?:="([^"]*)")?$/);
    if (match) {
      attributes[match[1]] = match[2] || '';
    }
  }

  // CRITICAL: Declare global variables BEFORE loading CDN scripts
  // Some CDN scripts (especially Klaviyo) use bare variable references like `collection`,
  // `taxonomy`, `product` that need to exist as declared variables, not just window properties.
  // We inject an inline script as the FIRST script in <head> to ensure these are declared.
  if (config.bool('klaviyo_status')) {
    const globalVarsScript = document.createElement('script');
    // Use simple var declarations without conditionals to ensure hoisting works
    // These create both window properties AND variable bindings in global scope
    globalVarsScript.textContent = `
      var collection = window.collection || null;
      var taxonomy = window.taxonomy || null;
      var product = window.product || null;
      var variant = window.variant || null;
      var cart = window.cart || null;
    `;
    // Insert as the FIRST script to ensure it runs before any CDN code
    const firstScript = document.head.querySelector('script');
    if (firstScript) {
      document.head.insertBefore(globalVarsScript, firstScript);
    } else {
      document.head.appendChild(globalVarsScript);
    }
  }

  // 1. Load core scripts. A blocked/failed script (ad blocker, network) must not
  // abort init — log and continue so the rest of the SDK still initializes.
  for (const script of CORE_SCRIPTS) {
    try {
      await loadScript(`${CDN_BASE}/${script}`, attributes);
    } catch (err) {
      console.warn(`[Analyzify] Failed to load core script ${script}:`, err);
    }
  }

  // 2. Initialize dataLayer, set consent defaults, and load Google scripts
  const ga4Status = config.bool('ga4_status') || config.bool('ga4_status', true);
  const gadsStatus = config.bool('gads_status') || config.bool('gads_status', true);
  const gtmStatus = config.bool('gtm_dataLayer');
  const resolvedGtmId = gtmContainerId || config.str('gtm_id');

  // Ensure dataLayer exists when ANY Google integration is active (GA4, GADS, or GTM)
  if (ga4Status || gadsStatus || gtmStatus) {
    (window as any).dataLayer = (window as any).dataLayer || [];
  }

  // 2a. Set Google Consent Mode v2 defaults BEFORE any Google scripts load
  // This MUST happen before gtag.js and GTM container are loaded.
  // Per Google's spec, consent defaults must be the first gtag() call.
  if (consentDefaults && (ga4Status || gadsStatus || gtmStatus)) {
    // Ensure gtag function exists for consent calls
    if (typeof (window as any).gtag !== 'function') {
      function gtag(..._args: any[]) {
        (window as any).dataLayer.push(arguments);
      }
      (window as any).gtag = gtag;
    }

    // Build consent default object (only include provided values)
    const consentConfig: Record<string, string | number | boolean> = {};
    if (consentDefaults.ad_storage) consentConfig.ad_storage = consentDefaults.ad_storage;
    if (consentDefaults.ad_user_data) consentConfig.ad_user_data = consentDefaults.ad_user_data;
    if (consentDefaults.analytics_storage) consentConfig.analytics_storage = consentDefaults.analytics_storage;
    if (consentDefaults.functionality_storage) consentConfig.functionality_storage = consentDefaults.functionality_storage;
    if (consentDefaults.personalization_storage) consentConfig.personalization_storage = consentDefaults.personalization_storage;
    if (consentDefaults.security_storage) consentConfig.security_storage = consentDefaults.security_storage;
    if (consentDefaults.wait_for_update !== undefined) consentConfig.wait_for_update = consentDefaults.wait_for_update;

    (window as any).gtag('consent', 'default', consentConfig);

    // Set ads_data_redaction if enabled
    if (consentDefaults.ads_data_redaction) {
      (window as any).gtag('set', 'ads_data_redaction', true);
    }
  }

  // 2b. Load Cookiebot CMP (consent banner)
  // No data-blockingmode attribute — Google Consent Mode v2 (configured above)
  // already handles tag blocking, so Cookiebot's script-level blocking is not needed.
  // Non-blocking — if it fails, tracking still works (just without consent banner).
  if (cookiebotId) {
    await loadScript(
      `https://consent.cookiebot.com/uc.js`,
      {
        ...attributes,
        'data-cbid': cookiebotId,
        id: 'Cookiebot',
        type: 'text/javascript',
        async: 'true',
      }
    ).catch(() => {
      console.warn('[Analyzify] Failed to load Cookiebot CMP, consent banner will not appear');
    });
  }

  // 2c. Load Shopify consent-tracking-api for headless storefronts
  // This provides the real setTrackingConsent() that sets consent cookies
  // Shopify's checkout reads these cookies to determine tracking consent
  if (storefrontRootDomain) {
    await loadScript(
      'https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.2/consent-tracking-api.js',
      { ...attributes, 'data-cookieconsent': 'ignore' }
    ).catch(() => {
      console.warn('[Analyzify] Failed to load Shopify consent-tracking-api');
    });

    // After script loads, wrap setTrackingConsent with headless params
    // and dispatch the consentTrackingApiLoaded event.
    // NOTE: The property watcher in shopify-globals.ts handles the case where
    // the script uses direct assignment, but v0.2 uses Object.defineProperty
    // which bypasses our setter. This post-load wrapping is the reliable path.
    const cp = (window as any).Shopify?.customerPrivacy;
    if (cp && typeof cp.setTrackingConsent === 'function') {
      const original = cp.setTrackingConsent;
      // checkoutRootDomain: used by the API for Storefront API requests
      // storefrontRootDomain: dot-prefixed for cross-subdomain cookies
      //   (Hydrogen pattern: '.example.com' so checkout can read storefront cookies)
      const dotPrefixedDomain = storefrontRootDomain && !storefrontRootDomain.startsWith('.')
        ? '.' + storefrontRootDomain
        : storefrontRootDomain;
      cp.setTrackingConsent = function (consent: any, callback?: any) {
        return original.call(this, {
          ...consent,
          headlessStorefront: true,
          checkoutRootDomain: checkoutRootDomain || storefrontRootDomain,
          storefrontRootDomain: dotPrefixedDomain,
          storefrontAccessToken,
        }, callback);
      };

      // Make an initial setTrackingConsent call to register the headless
      // storefront with the API. This triggers the API to fetch the shop's
      // privacy settings and perform visitor geolocation. Without this call,
      // userCanBeTracked() always returns true because the API is passive.
      //
      // NOTE: Hydrogen doesn't make this call — it relies on Shopify's privacy
      // banner (loadBanner). Since we use Cookiebot instead, we must trigger
      // region detection ourselves.
      //
      // Non-blocking with timeout: if Shopify's servers are unreachable
      // (VPN, ad blockers, network issues), we don't block SDK init.
      await new Promise<void>((resolve) => {
        const timeout = setTimeout(resolve, 3000);
        cp.setTrackingConsent({}, () => {
          clearTimeout(timeout);
          resolve();
        });
      });
    }
    document.dispatchEvent(new CustomEvent('consentTrackingApiLoaded'));
  }

  // Load Google gtag.js if GA4 or GADS is enabled
  // This must be loaded before the an_ga4_gads.js CDN script
  if (ga4Status || gadsStatus) {
    const measurementId = config.str('ga4_id') || config.str('gads_id');
    if (measurementId) {
      function gtag(..._args: any[]) {
        (window as any).dataLayer.push(arguments);
      }
      (window as any).gtag = gtag;

      // Load Google's gtag.js
      // data-cookieconsent="ignore" prevents Cookiebot auto-blocking from blocking this script.
      // Consent is handled by Google Consent Mode (gtag consent default/update), not by script blocking.
      await loadScript(
        `https://www.googletagmanager.com/gtag/js?id=${measurementId}`,
        { ...attributes, async: 'true', 'data-cookieconsent': 'ignore' }
      ).catch(() => {
        console.warn('[Analyzify] Failed to load gtag.js, GA4 tracking may not work');
      });

      // Initialize gtag with config
      gtag('js', new Date());

      // Note: Don't call gtag('config', ...) here - let the CDN scripts handle configuration
    }
  }

  // Load GTM container if GTM is enabled.
  // Supports two modes:
  // 1. gtmScriptUrl: Full Stape script URL (e.g., https://ss.example.com/xxx.js?key=yyy)
  //    Stape uses its own container IDs, not standard GTM IDs.
  // 2. gtmContainerId + gtmServerUrl: Standard GTM format (gtm.js?id=GTM-XXX)
  //    Falls back to googletagmanager.com if no server URL provided.
  if (gtmStatus && (gtmScriptUrl || resolvedGtmId)) {
    // GTM snippet: initializes the dataLayer start event (matches standard GTM snippet)
    (window as any).dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const gtmUrl = gtmScriptUrl
      || `${gtmServerUrl || 'https://www.googletagmanager.com'}/gtm.js?id=${resolvedGtmId}`;

    // data-cookieconsent="ignore" prevents Cookiebot auto-blocking from blocking this script.
    // Consent is handled by Google Consent Mode (gtag consent default/update), not by script blocking.
    await loadScript(
      gtmUrl,
      { ...attributes, async: 'true', 'data-cookieconsent': 'ignore' }
    ).catch(() => {
      console.warn(`[Analyzify] Failed to load GTM container (${gtmUrl}), GTM tracking may not work`);
    });
  }

  // 3. Load platform scripts based on config
  const platformLoads: Promise<void>[] = [];

  // GA4 + Google Ads (combined script)
  if (ga4Status || gadsStatus) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.ga4_gads}`, attributes)
    );
  }

  // Facebook
  if (config.bool('meta_status') || config.bool('meta_status', true)) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.facebook}`, attributes)
    );
  }

  // GTM
  if (config.bool('gtm_dataLayer')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.gtm}`, attributes)
    );
  }

  // TikTok
  if (config.bool('tiktok_status')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.tiktok}`, attributes)
    );
  }

  // Pinterest
  if (config.bool('pinterest_status')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.pinterest}`, attributes)
    );
  }

  // Bing
  if (config.bool('bing_status')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.bing}`, attributes)
    );
  }

  // Snapchat
  if (config.bool('snapchat_status')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.snapchat}`, attributes)
    );
  }

  // Klaviyo
  if (config.bool('klaviyo_status')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.klaviyo}`, attributes)
    );
  }

  // Criteo
  if (config.bool('criteo_status')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.criteo}`, attributes)
    );
  }

  // X (Twitter)
  if (config.bool('twitter_status')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.x}`, attributes)
    );
  }

  // Clarity
  if (config.bool('clarity_status')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.clarity}`, attributes)
    );
  }

  // Hotjar
  if (config.bool('hotjar_status')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.hotjar}`, attributes)
    );
  }

  // Server-side
  if (config.bool('server_side_status')) {
    platformLoads.push(
      loadScript(`${CDN_BASE}/${PLATFORM_SCRIPTS.gtag_ss}`, attributes)
    );
  }

  // Wait for all platform scripts
  // Isolate per-script failures: one blocked platform script (e.g. an ad
  // blocker blocking an_tiktok.js) must not prevent the others from loading
  // or abort SDK init.
  await Promise.allSettled(platformLoads);

  // 4. Load helper scripts
  for (const script of HELPER_SCRIPTS) {
    await loadScript(`${CDN_BASE}/${script}`, attributes).catch(() => {
      // Helper scripts are optional, don't fail if they don't load
      console.warn(`Optional script failed to load: ${script}`);
    });
  }

  // 5. Load third-party integration scripts (optional)
  const thirdPartyLoads: Promise<void>[] = [];

  // PageFly - Landing page builder
  if (config.bool('pageFly')) {
    thirdPartyLoads.push(
      loadScript(`${CDN_BASE}/${THIRD_PARTY_SCRIPTS.pageFly}`, attributes)
        .catch(() => console.warn('[Analyzify] PageFly script failed to load'))
    );
  }

  // Rebuy - Cart upsells and recommendations
  if (config.bool('rebuy_atc_rfc')) {
    thirdPartyLoads.push(
      loadScript(`${CDN_BASE}/${THIRD_PARTY_SCRIPTS.rebuy}`, attributes)
        .catch(() => console.warn('[Analyzify] Rebuy script failed to load'))
    );
  }

  // Shogun - Page builder
  if (config.bool('shogun')) {
    thirdPartyLoads.push(
      loadScript(`${CDN_BASE}/${THIRD_PARTY_SCRIPTS.shogun}`, attributes)
        .catch(() => console.warn('[Analyzify] Shogun script failed to load'))
    );
  }

  // Wait for third-party scripts (optional, don't fail if they don't load)
  await Promise.allSettled(thirdPartyLoads);
}
